import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { useBrand } from '@atc/brand';
import { useFeatures } from '@atc/features';
import { sendClick } from 'reaxl-analytics';
import { ndsEsntialBuyOnlineClick } from 'reaxl-analytics-handlers';
import { AnimatedButton, Button, DisclaimerText } from 'reaxl';

import { debugWallet, useWalletCalcConfig } from 'wallet-manager';

import { saveDeal, getDeal } from 'wallet-deals';

import getEcommLinkWithWalletData from '../utilities/getEcommLinkWithWalletData';
import getTransformedEcommLink from '../utilities/getTransformedEcommLink';
import getChatSessionToken from '../utilities/getChatSessionToken';

// TODO: Write JSDoc for this component (compare & contrast with DigitalRetailOverlayCTA component)
function EsntialBuyNowCTA({
    analyticsData,
    bsStyle = 'secondary',
    className,
    saveDealXgFetcher,
    dealsDuck,
    disclaimer,
    env,
    href = '',
    label = 'Continue Online',
    listing,
    nationalRates,
    onClick = () => {},
    ownerTrackingId,
    paymentsDuck,
    zip = '',
    ...rest
}) {
    const {
        brand: [, { consumer_brand: consumerBrand }],
        dealer_website_link: [transformDealerWebsiteLink, dealerWebsiteConfig],
    } = useFeatures(['brand', 'dealer_website_link']);

    const brand = useSelector((state) => state?.brand || '');
    const { brands } = useBrand();
    const shouldRemoveUtmParams = [brands.ATC_BRAND, brands.KBB_BRAND].includes(brand);

    // not destructuring or defaulting listing due downstream rules for defined or undefined object
    const owner = listing?.owner ?? {};
    const parentId = listing?.marketExtension?.parentOwnerId;
    const fallbackOwnerId = parentId ?? (typeof owner === 'number' ? owner : owner.id);
    const dealerId = ownerTrackingId ?? fallbackOwnerId;

    const ecommercePaymentsInfo = useSelector((state) =>
        paymentsDuck.selectors.getEcommercePaymentsInfo(state, true, listing?.pricingDetail)
    );

    const { id: dealXgId } =
        useSelector((state) => dealsDuck?.selectors?.getDealByListingVin(state, listing?.vin)) || {};
    const payments = useSelector(paymentsDuck?.selectors.getDuckState);

    const { cashDealPrice, disableTrade } = useWalletCalcConfig(payments, listing);

    const [buttonState, setButtonState] = useState(null);

    let baseUrl;
    if (typeof window !== 'undefined') {
        baseUrl = window?.location?.href;
    }

    if (!href) return null;

    const getEcommHref = async (existingDealXgId) => {
        // Get chat session token before constructing eComm URL
        const chatSessionId = await getChatSessionToken('EsntialBuyNowCTA');
        debugWallet('UDS', 'AE chat session ID:', chatSessionId);

        if (existingDealXgId) {
            return getTransformedEcommLink(
                href,
                label,
                existingDealXgId,
                transformDealerWebsiteLink ? dealerWebsiteConfig : {},
                baseUrl,
                dealerId,
                shouldRemoveUtmParams,
                chatSessionId
            );
        }
        return getEcommLinkWithWalletData(href, ecommercePaymentsInfo, zip, baseUrl, dealerId, chatSessionId);
    };

    const handleClick = async (event) => {
        event.stopPropagation();
        event.preventDefault();
        sendClick(ndsEsntialBuyOnlineClick, event, analyticsData);
        onClick();

        let existingDealXgId;
        if (!dealXgId && !!saveDealXgFetcher) {
            const { deal } = getDeal(listing, payments, nationalRates, false, cashDealPrice, disableTrade);
            const saveIcoData = payments?.tradeIn?.ico?.length;

            setButtonState('loading');

            const response = await saveDeal({
                consumerBrand,
                listing,
                deal,
                zip,
                walletId: payments?.walletId || null,
                componentName: 'EsntialBuyNowCTA',
                saveDealXgFetcher,
                isEsntial: true,
                icoTradeIn: saveIcoData ? payments?.tradeIn?.ico?.[0] : undefined,
                env,
            });

            setButtonState('loaded');
            if (response && typeof response === 'string') {
                existingDealXgId = response;
            } else {
                // eslint-disable-next-line no-console
                console.log('Deal creation failed for esntial');
            }
        } else {
            existingDealXgId = dealXgId;
        }

        const ecommHref = await getEcommHref(existingDealXgId);
        if (ecommHref) {
            debugWallet('UDS', 'Esntial Deeplink URL:', ecommHref);
            window.open(ecommHref, '_self', 'nofollow, noopener');
        }
    };

    return (
        <>
            {saveDealXgFetcher ? (
                <AnimatedButton
                    bsStyle={bsStyle}
                    buttonState={buttonState}
                    buttonText={label}
                    className={className}
                    data-cmp="animatedEsntialCTA"
                    loadedText={label}
                    onClick={handleClick}
                    type="button"
                    {...rest}
                />
            ) : (
                <Button
                    data-cmp="esntialCTA"
                    bsStyle={bsStyle}
                    onClick={handleClick}
                    className={className}
                    {...rest}
                >
                    {label}
                </Button>
            )}
            {!!disclaimer && (
                <DisclaimerText
                    className="text-center text-inherit margin-bottom-4"
                    componentClass="div"
                >
                    {disclaimer}
                </DisclaimerText>
            )}
        </>
    );
}

EsntialBuyNowCTA.propTypes /* remove-proptypes */ = {
    /**
     * Contains props like listing id, owner id, and sometimes parent id for analytics click
     */
    analyticsData: PropTypes.object,
    /**
     * Style of the button, works when uiContext = default
     */
    bsStyle: PropTypes.oneOf(['default', 'link', 'primary', 'secondary']),
    /**
     * The className to apply to the root node
     */
    className: PropTypes.string,
    /**
     * A disclaimer to display below the button
     */
    disclaimer: PropTypes.string,
    /**
     * link where the website is directed to, if not passed in renders null
     */
    href: PropTypes.string.isRequired,
    /**
     * Label to display, unless circle button
     */
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    /**
     * callback for website click
     */
    onClick: PropTypes.func,
    /**
     * The vehicle listing
     */
    listing: PropTypes.object,
    /**
     * nationalRates for deal calculation
     */
    nationalRates: PropTypes.object,
    /**
     * owner id for esntial tracking ME parent fallback to base owner id
     */
    ownerTrackingId: PropTypes.string,
    /**
     * zip code value
     */
    zip: PropTypes.string,
    paymentsDuck: PropTypes.object,
};

export default EsntialBuyNowCTA;
